import { AlertCircle, Eye, EyeOff, Lock, Mail, ShieldCheck } from 'lucide-react';
import { useEffect, useState, type FormEvent } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import revlvLogo from '../assets/revlv-logo.png';
import { useAuth } from '../auth/AuthContext';
import { startCooldown } from '../auth/signInCooldown';
import { SignInCardSlide } from './SignInCardSlide';
import { SignInPinStep } from './SignInPinStep';
import type { PinChallengeIssued } from '../features/auth/authTypes';
import { Button } from '../components/Button';
import { Wordmark } from '../components/Wordmark';
import { TextField } from '../components/inputs/TextField';
import { ApiError } from '../lib/apiError';

interface LocationState {
  from?: string;
}

/**
 * Paths that must never be used as a post-sign-in destination.
 *
 * `/change-password` matters in particular: completing a password change signs the user
 * out, which makes ProtectedRoute record `/change-password` as "where they were headed".
 * Honouring that would drop them back on the page they just finished. `/login` is here for
 * the same class of reason — returning to the sign-in page after signing in is a loop.
 */
const NON_DESTINATIONS = ['/login', '/change-password'];

/** Only same-site relative paths are honoured, so `from` cannot be used as an open redirect. */
function resolveDestination(from: string | undefined): string {
  if (!from || !from.startsWith('/') || from.startsWith('//')) {
    return '/';
  }

  const path = from.split('?')[0];

  return NON_DESTINATIONS.includes(path) ? '/' : from;
}

export function Login() {
  const { user, login, adoptSession, signingIn } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState<ApiError | null>(null);
  const [submitted, setSubmitted] = useState(false);

  /*
    The challenge is what decides which panel the card shows.

    Held here rather than in a route of its own: it is the SAME sign-in, half finished, and a
    URL for it would be a page a reader could bookmark, reload and land on holding a ticket the
    server has forgotten. Losing it on a refresh is correct — the password is what buys another.
  */
  const [challenge, setChallenge] = useState<PinChallengeIssued | null>(null);

  const destination = resolveDestination((location.state as LocationState | null)?.from);

  // A signed-in user landing here (back button, bookmarked /login) goes where they meant to.
  useEffect(() => {
    if (user) {
      navigate(user.mustChangePassword ? '/change-password' : destination, { replace: true });
    }
  }, [user, navigate, destination]);

  async function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setSubmitted(true);
    setError(null);

    if (!email.trim() || !password) {
      return;
    }

    try {
      const outcome = await login({ email: email.trim(), password });

      if (outcome.challenge) {
        setChallenge(outcome.challenge);

        // The password has done its work and must not sit in state behind a panel the reader
        // is no longer looking at.
        setPassword('');
        return;
      }
    } catch (caught) {
      const failure =
        caught instanceof ApiError
          ? caught
          : new ApiError('Could not reach the server. Check your connection and try again.', 0);

      /*
        The cooldown gets a PAGE, not a line of red text.

        The sentence a 429 carries — "try again in 47 seconds" — stops being true as it is
        read, and a form left sitting under it invites the reader to press the button again
        and be refused again. The page says how long and takes them back by itself.

        It is a courtesy and not the control: the server refuses the request whether or not
        anybody is looking at the countdown.
      */
      if (failure.isThrottled && failure.retryAfterSeconds !== null) {
        startCooldown(failure.retryAfterSeconds);
        navigate('/cooldown', { replace: true });
        return;
      }

      setError(failure);
      // Clear only the password: making the user retype their address after a typo in the
      // password is a small cruelty repeated on every failed attempt.
      setPassword('');
    }
  }

  const emailError = submitted && !email.trim() ? 'Enter your email address.' : undefined;
  const passwordError = submitted && !password ? 'Enter your password.' : undefined;

  return (
    <main className="flex min-h-screen items-center justify-center bg-slate-100 px-4 py-10">
      <div className="w-full max-w-sm">
        {/*
          Brand block. Identity first, so the user knows what they are signing in to — the
          mark and the product's name, and nothing else. The tagline under it went on
          15 Sep 2026: a sign-in page is not where a product is described to someone, and the
          reader is here to get in rather than to find out what this is.

          `items-center` on the column is what centres the name, so the heading needs no
          `text-center` of its own and no wrapper to carry one.
        */}
        <div className="mb-8 flex flex-col items-center gap-3">
          <div className="flex h-12 w-12 items-center justify-center rounded-panel bg-brand-500 shadow-raised">
            <ShieldCheck className="h-6 w-6 text-white" aria-hidden="true" />
          </div>
          <h1 className="text-lg font-semibold text-slate-800"><Wordmark /></h1>
        </div>

        {/*
          The card carries the form and nothing above it.

          Its heading said "Sign in" over a button that says "Sign in" — the same value in two
          slots, three inches apart — and the line under it instructed a reader who has already
          been handed an account and is holding it. Both went on 15 Sep 2026.

          **A COLUMN with a gap, not margins.** The form's `mt-5` and the error's `mt-4` each
          measured from something above them, and with the heading gone they measured from the
          card's own padding — so the first field sat 44px down an otherwise empty card. A gap
          spaces whatever is actually there, which is the point: the error region comes and
          goes, and the spacing must not depend on whether it is drawn.
        */}
        <div className="rounded-panel border border-slate-200 bg-white p-6 shadow-panel">
          <SignInCardSlide
            showSecond={challenge !== null}
            second={
              challenge ? (
                <SignInPinStep
                  challenge={challenge}
                  onSignedIn={adoptSession}
                  /*
                    Back throws the ticket away rather than keeping it for a second try. A
                    ticket the reader has walked away from is one the server will expire anyway,
                    and holding it would let a half-finished sign-in sit on screen indefinitely.
                  */
                  onBack={() => {
                    setChallenge(null);
                    setSubmitted(false);
                    setError(null);
                  }}
                />
              ) : null
            }
            first={
        <div className="flex flex-col gap-5">
          {/*
            One error region, announced to assistive technology. The server returns the same
            message for every credential failure, so this never reveals whether an account
            exists.
          */}
          {error ? (
            <div
              role="alert"
              className="flex items-start gap-2 rounded-lg border border-red-200 bg-red-50 p-3"
            >
              <AlertCircle className="mt-0.5 h-4 w-4 shrink-0 text-red-600" aria-hidden="true" />
              <div className="min-w-0">
                <p className="text-xs text-red-700">{error.message}</p>
                {error.correlationId ? (
                  <p className="mt-1 font-mono tabular-nums text-[11px] text-red-500">
                    Reference {error.correlationId}
                  </p>
                ) : null}
              </div>
            </div>
          ) : null}

          <form onSubmit={handleSubmit} noValidate className="flex flex-col gap-4">
            <TextField
              id="login-email"
              label="Email"
              type="email"
              autoComplete="username"
              autoFocus
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              error={emailError}
              leading={<Mail className="h-4 w-4" aria-hidden="true" />}
              placeholder="you@revlv.com"
              disabled={signingIn}
            />

            <TextField
              id="login-password"
              label="Password"
              type={showPassword ? 'text' : 'password'}
              autoComplete="current-password"
              required
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              error={passwordError}
              leading={<Lock className="h-4 w-4" aria-hidden="true" />}
              disabled={signingIn}
              trailing={
                <button
                  type="button"
                  id="login-toggle-password"
                  onClick={() => setShowPassword((v) => !v)}
                  className="rounded-md p-1.5 text-slate-400 transition hover:bg-slate-100 hover:text-slate-600 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-200"
                  aria-label={showPassword ? 'Hide password' : 'Show password'}
                  aria-pressed={showPassword}
                  tabIndex={-1}
                >
                  {showPassword ? (
                    <EyeOff className="h-4 w-4" aria-hidden="true" />
                  ) : (
                    <Eye className="h-4 w-4" aria-hidden="true" />
                  )}
                </button>
              }
            />

            <Button
              id="login-submit"
              type="submit"
              variant="primary"
              fullWidth
              loading={signingIn}
              loadingLabel="Signing in"
              className="mt-1"
            >
              Sign in
            </Button>
          </form>
        </div>
            }
          />
        </div>

        {/*
          Who built this, under the card rather than in the brand block above it — that block
          names the PRODUCT the reader is signing in to, and this names its maker. The wordmark
          carries the name, so the image is decorative and the alt text is empty: announcing
          "Revlv Solutions Inc." twice is the same value in two slots.
        */}
        <div className="mt-6 flex items-center justify-center gap-2">
          <img src={revlvLogo} alt="" aria-hidden="true" className="h-5 w-5 shrink-0" />
          <span className="text-xs text-slate-600">Revlv Solutions Inc.</span>
        </div>
      </div>
    </main>
  );
}
